"use client";

/**
 * Guest Guard Component
 * @apal/shared-auth
 *
 * Protects routes that should only be accessible to unauthenticated users.
 * Redirects to dashboard if user is already authenticated.
 */

import * as React from "react";
import type { GuestGuardProps } from "../types";
import { useAuth } from "../context";

/**
 * Extended props with navigation callbacks
 */
export interface GuestGuardComponentProps extends GuestGuardProps {
  /** Callback when user is already authenticated */
  onAuthenticated?: () => void;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
}

/**
 * GuestGuard - Protects routes for unauthenticated users only
 *
 * @example
 * ```tsx
 * // With navigation callback
 * <GuestGuard onAuthenticated={() => navigate("/dashboard")}>
 *   <LoginPage />
 * </GuestGuard>
 *
 * // With redirect path (handled by callback)
 * <GuestGuard
 *   redirectTo="/apal/dashboard"
 *   onAuthenticated={() => navigate(redirectTo)}
 * >
 *   <RegisterPage />
 * </GuestGuard>
 * ```
 */
export function GuestGuard({
  children,
  onAuthenticated,
  loadingComponent,
}: GuestGuardComponentProps): React.JSX.Element | null {
  const { isAuthenticated, isLoading } = useAuth();

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    // Already authenticated - call authenticated callback
    if (isAuthenticated) {
      onAuthenticated?.();
      return;
    }
  }, [isAuthenticated, isLoading, onAuthenticated]);

  // Still loading
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return null;
  }

  // Already authenticated - don't render children
  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
